import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import blogs from '../data/blogs';
import './BlogDetailPage.css';

const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ArrowLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const ArticleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );

const BlogDetailPage = () => {
  const { id } = useParams();
  const blog = blogs.find((b) => b.id === id || b.slug === id || String(b.id) === id);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (blog) {
      document.title = blog.metaTitle || blog.title;

      // Meta Description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = "description";
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = blog.metaDescription || blog.excerpt;

      // Canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = blog.canonicalUrl || window.location.href;

      // JSON-LD Schema
      let script = document.querySelector('#blog-schema');
      if (blog.schema) {
        if (!script) {
          script = document.createElement('script');
          script.id = 'blog-schema';
          script.type = 'application/ld+json';
          document.head.appendChild(script);
        }
        script.innerHTML = JSON.stringify(blog.schema);
      } else if (script) {
        script.remove();
      }
    }

    return () => {
      // Cleanup on unmount if needed, though typically canonicals/descriptions stick around or are overwritten by other pages
    };
  }, [blog]);

  if (!blog) {
    return (
      <div className="blog-detail-page">
        <Navbar />
        <div className="blog-detail-error">
          <h2>Blog post not found</h2>
          <Link to="/blog" className="back-link">
            <ArrowLeft /> Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="blog-detail-page">
      <Navbar />

      <article className="blog-detail-article">
        <div className="blog-detail-header">
          <Link to="/blog" className="back-link">
            <ArrowLeft /> Back to Insights
          </Link>
          <div className="blog-detail-category">{blog.category}</div>
          <h1>{blog.title}</h1>
          <div className="blog-detail-meta">
            {blog.author && (
              <span className="meta-item">
                <UserIcon /> {blog.author}
              </span>
            )}
            <span className="meta-item">
              <CalendarIcon /> {blog.date}
            </span>
            <span className="meta-item">
              <ClockIcon /> {blog.readTime}
            </span>
          </div>
        </div>

        <div className="blog-detail-hero-image">
          {blog.image ? (
            <img src={blog.image} alt={blog.title} />
          ) : (
            <div className="blog-detail-img-placeholder">
              <ArticleIcon />
            </div>
          )}
        </div>

        <div className="blog-detail-content-container">
          <div 
            className="blog-detail-content" 
            dangerouslySetInnerHTML={{ __html: blog.content }} 
          />
          
          <div className="blog-detail-share">
            <h3>Share this article</h3>
            <div className="share-buttons">
              {/* Placeholder for share buttons */}
              <button className="share-btn">Twitter</button>
              <button className="share-btn">LinkedIn</button>
              <button className="share-btn">Facebook</button>
            </div>
          </div>
        </div>
      </article>

      <section className="more-blogs-section">
        <div className="more-blogs-inner">
            <h2>Read More Insights</h2>
            <div className="more-blogs-grid">
                {blogs
                    .filter((b) => b.id !== blog.id)
                    .slice(0, 3)
                    .map((other) => (
                        <Link to={`/blog/${other.id}`} key={other.id} className="other-blog-card">
                            <div className="other-blog-info">
                                <span className="other-blog-category">{other.category}</span>
                                <h3>{other.title}</h3>
                            </div>
                        </Link>
                    ))
                }
            </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
